import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  TextField,
  Button,
  Chip,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { 
  FileText as DocIcon,
  AlertTriangle as RiskIcon
} from 'lucide-react';

// Define the interface for proposal data
interface ProposalData {
  id: string;
  title: string;
  description: string;
  status: 'Draft' | 'Pending' | 'Approved' | 'Rejected';
  cost: number;
  createdAt: string;
  timelineType?: 'Short' | 'Medium' | 'Long';
  estimatedTimeline?: number;
  priority?: 'Low' | 'Medium' | 'High';
  riskLevel?: 'Low' | 'Medium' | 'High';
}

interface EditableProposalCardProps {
  initialProposal: ProposalData;
  onSave: (updatedProposal: ProposalData) => void;
  onSubmitToAI?: (proposal: ProposalData) => Promise<void>;
  onClick?: () => void;
}

const StyledCard = styled(Card)(({ theme }) => ({
    background: 'rgba(26, 26, 46, 0.6)',
    backdropFilter: 'blur(10px)',
    borderRadius: theme.spacing(2),
    border: '1px solid rgba(138, 43, 226, 0.2)',
    padding: theme.spacing(3),
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 255, 255, 0.3))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
  },
  '&:hover': {
    background: 'rgba(26, 26, 46, 0.8)',
    transform: 'scale(1.02)',
    boxShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
    border: '1px solid rgba(0, 255, 255, 0.3)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  },
}));

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => {
  let color = '#8A2BE2'; // Default purple
  let bgColor = 'rgba(138, 43, 226, 0.1)';
  
  if (status === 'Draft') {
    color = '#FFD700'; // Gold
    bgColor = 'rgba(255, 215, 0, 0.1)';
  } else if (status === 'Pending') {
    color = '#00BFFF'; // Deep sky blue
    bgColor = 'rgba(0, 191, 255, 0.1)';
  } else if (status === 'Approved') {
    color = '#00CC99'; // Teal
    bgColor = 'rgba(0, 204, 153, 0.1)';
  } else if (status === 'Rejected') {
    color = '#FF4081'; // Pink
    bgColor = 'rgba(255, 64, 129, 0.1)';
  }
  
  return {
    backgroundColor: bgColor,
    color: color,
    fontWeight: 'bold',
    borderRadius: '4px',
    border: `1px solid ${color}`
  };
});

const MetadataItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  color: 'rgba(255, 255, 255, 0.7)',
}));

const fieldStyles = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.7)',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiSelect-icon': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
};

const EditableProposalCard: React.FC<EditableProposalCardProps> = ({
  initialProposal,
  onSave,
  onSubmitToAI,
  onClick,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [proposal, setProposal] = useState<ProposalData>(initialProposal);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleToggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleSave = () => {
    onSave(proposal);
    setIsEditing(false);
  };

  const handleChange = (field: keyof ProposalData, value: any) => {
    setProposal(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const submitToAI = async () => {
    setIsSubmitting(true);
    
    try {
      if (onSubmitToAI) {
        await onSubmitToAI(proposal);
      } else {
        console.log('Submitting proposal to AI service:', proposal);
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        const updatedProposal = {
          ...proposal,
          status: 'Pending' as const
        };
        
        setProposal(updatedProposal);
        onSave(updatedProposal);
        
        alert('Proposal successfully submitted to AI for processing!');
      }
    } catch (error) {
      console.error('Error submitting to AI:', error);
      alert('Failed to submit proposal. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StyledCard onClick={isEditing ? undefined : onClick}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {isEditing ? (
            <TextField
              fullWidth
              variant="outlined"
              label="Title"
              value={proposal.title}
              onChange={(e) => handleChange('title', e.target.value)}
              sx={{ ...fieldStyles, mr: 2 }}
            />
          ) : (
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              {proposal.title}
            </Typography>
          )}
          
          
          <Box>
            {isEditing ? (
              <IconButton onClick={handleSave} sx={{ color: '#00CC99' }}>
                <SaveIcon />
              </IconButton>
            ) : (
              <IconButton onClick={handleToggleEdit} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {isEditing ? (
          <TextField
            fullWidth
            rows={1}
            variant="outlined"
            label="Description"
            value={proposal.description}
            onChange={(e) => handleChange('description', e.target.value)}
            sx={{ ...fieldStyles, mb: 2 }}
          />
        ) :
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            label="Description"
            value={proposal.description}
            onChange={(e) => handleChange('description', e.target.value)}
            sx={{ ...fieldStyles, mb: 2 }}
          />
        }

        <Grid container spacing={2}>
          <Grid item xs={6}>
            {isEditing ? (
              <FormControl fullWidth variant="outlined" sx={{ ...fieldStyles, mb: 2 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={proposal.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="Draft">Draft</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <StatusChip
                label={proposal.status}
                status={proposal.status}
              />
            )}
          </Grid>
          
          <Grid item xs={6}>
            {isEditing ? (
              <TextField
                fullWidth
                variant="outlined"
                label="Cost"
                type="number"
                value={proposal.cost}
                onChange={(e) => handleChange('cost', Number(e.target.value))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon sx={{ color: '#00CC99' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ ...fieldStyles }}
              />
            ) : (
              <MetadataItem>
                <MoneyIcon sx={{ mr: 1, color: '#00CC99' }} />
                <Typography variant="body2">
                  ${proposal.cost.toLocaleString()}
                </Typography>
              </MetadataItem>
            )}
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {isEditing ? (
                <FormControl fullWidth variant="outlined" sx={{ ...fieldStyles, mb: 2 }}>
                  <InputLabel>Timeline Type</InputLabel>
                  <Select
                    value={proposal.timelineType || 'Medium'}
                    onChange={(e) => handleChange('timelineType', e.target.value)}
                    label="Timeline Type"
                  >
                    <MenuItem value="Short">Short</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="Long">Long</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <MetadataItem>
                  <TimeIcon sx={{ mr: 1, color: '#FF6F00' }} />
                  <Typography variant="body2">
                    Timeline: {proposal.timelineType || 'Medium'}
                  </Typography>
                </MetadataItem>
              )}
            </Grid>
            
            <Grid item xs={6}>
              {isEditing ? (
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Est. Timeline (weeks)"
                  type="number"
                  value={proposal.estimatedTimeline || 4}
                  onChange={(e) => handleChange('estimatedTimeline', Number(e.target.value))}
                  sx={{ ...fieldStyles }}
                />
              ) : (
                <MetadataItem>
                  <CalendarIcon sx={{ mr: 1, color: '#8A2BE2' }} />
                  <Typography variant="body2">
                    Est. {proposal.estimatedTimeline || 4} weeks
                  </Typography>
                </MetadataItem>
              )}
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {isEditing ? (
                <FormControl fullWidth variant="outlined" sx={{ ...fieldStyles }}>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={proposal.priority || 'Medium'}
                    onChange={(e) => handleChange('priority', e.target.value)}
                    label="Priority"
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <MetadataItem>
                  <DocIcon size={18} style={{ marginRight: 8, color: '#00BFFF' }} />
                  <Typography variant="body2">
                    Priority: {proposal.priority || 'Medium'}
                  </Typography>
                </MetadataItem>
              )}
            </Grid>
            
            <Grid item xs={6}>
              {isEditing ? (
                <FormControl fullWidth variant="outlined" sx={{ ...fieldStyles }}>
                  <InputLabel>Risk Level</InputLabel>
                  <Select
                    value={proposal.riskLevel || 'Low'}
                    onChange={(e) => handleChange('riskLevel', e.target.value)}
                    label="Risk Level"
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <MetadataItem>
                  <RiskIcon size={18} style={{ marginRight: 8, color: '#FF4081' }} />
                  <Typography variant="body2">
                    Risk: {proposal.riskLevel || 'Low'}
                  </Typography>
                </MetadataItem>
              )}
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Created: {formatDate(proposal.createdAt)}
          </Typography>
          
          <Box>
            <Tooltip title="Submit to AI for analysis and improvement">
              <IconButton 
                onClick={(e) => {
                  e.stopPropagation();
                  submitToAI();
                }} 
                sx={{ 
                  color: '#00CC99',
                  '&:hover': {
                    color: '#00FF99',
                    background: 'rgba(0, 204, 153, 0.1)',
                  }
                }}
                disabled={isSubmitting}
              >
                <SendIcon />
              </IconButton>
            </Tooltip>
            
            {isEditing && (
              <Button 
                variant="contained" 
                onClick={handleSave}
                sx={{
                  background: 'linear-gradient(45deg, #00CC99 30%, #8A2BE2 90%)',
                  color: '#fff',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #00CC99 40%, #8A2BE2 100%)',
                  }
                }}
              >
                Save Changes
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default EditableProposalCard; 